import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { fmtNum, fmtDate } from "@/lib/format";
import { PageHeader } from "@/components/common/Page";
import { StatusBadge } from "@/components/common/StatusBadge";
import { FormDialog } from "@/components/common/FormDialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Plus, Pencil, Ship } from "lucide-react";
import { opt } from "@/lib/lookups";

export default function Products() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const { data: rows } = useQuery({ queryKey: ["products"], queryFn: async () => (await api.get("/products")).data });

  const save = useMutation({
    mutationFn: async (p) => editing ? api.put(`/products/${editing.id}`, p) : api.post("/products", p),
    onSuccess: () => { qc.invalidateQueries(); toast.success("Product saved"); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  const fields = [
    { name: "name", label: "Product Name", full: true },
    { name: "code", label: "Product Code" },
    { name: "product_type", label: "Product Type", type: "select", options: opt(["saas", "custom", "internal", "services"]), default: "saas" },
    { name: "status", label: "Status", type: "select", options: opt(["active", "beta", "sunset", "planned"]), default: "active" },
    { name: "launch_date", label: "Launch Date", type: "date" },
    { name: "description", label: "Description", type: "textarea", full: true },
    { name: "notes", label: "Notes", type: "textarea", full: true },
  ];

  return (
    <div className="ns-fade">
      <PageHeader title="Products" subtitle="NavScora product portfolio" testId="products-header">
        <Button onClick={() => { setEditing(null); setOpen(true); }} className="bg-gradient-to-r from-cyan-500 to-sky-600 font-semibold text-slate-900 hover:from-cyan-400 hover:to-sky-500" data-testid="add-product-button">
          <Plus className="mr-1.5 h-4 w-4" /> Add Product
        </Button>
      </PageHeader>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
        {(rows || []).map((p, i) => (
          <div key={p.id} className="ns-rise group cursor-pointer rounded-xl border border-[#1E2D4A] bg-[#17233B]/70 p-5 transition-all hover:border-cyan-500/40 hover:bg-[#1E2D4A]/60"
            style={{ animationDelay: `${i * 50}ms` }} onClick={() => navigate(`/products/${p.id}`)} data-testid={`product-card-${p.id}`}>
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-2.5">
                <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-cyan-500/10"><Ship className="h-4 w-4 text-cyan-400" /></div>
                <div>
                  <div className="font-semibold text-slate-100">{p.name}</div>
                  <div className="text-[11px] uppercase tracking-wide text-slate-500">{p.code}</div>
                </div>
              </div>
              <StatusBadge status={p.status} label={p.status} />
            </div>
            <p className="mt-3 line-clamp-2 text-sm text-slate-400">{p.description || "—"}</p>
            <div className="mt-4 flex items-center justify-between border-t border-[#1E2D4A] pt-3">
              <span className="text-xs text-slate-500 capitalize">{p.product_type}</span>
              <span className="text-xs text-slate-400">{fmtNum(p.customer_count)} customers</span>
            </div>
            <div className="mt-2 flex items-center justify-between">
              <span className="text-[11px] text-slate-600">Launched {fmtDate(p.launch_date)}</span>
              <Button variant="ghost" size="icon" className="h-7 w-7 text-slate-400 hover:text-cyan-300" onClick={(e) => { e.stopPropagation(); setEditing(p); setOpen(true); }} data-testid={`edit-product-${p.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
            </div>
          </div>
        ))}
      </div>
      <FormDialog open={open} onOpenChange={setOpen} title={editing ? "Edit Product" : "Add Product"} fields={fields} initial={editing} onSubmit={(p) => save.mutateAsync(p).then(() => true).catch(() => false)} testId="product-form" />
    </div>
  );
}
